import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { timesheetsAPI, exportAPI } from '../lib/api';
import { formatDate, formatHours, formatDistance, getStatusClass, capitalize, getWeekStart } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Clock,
  Calendar,
  Car,
  FileText,
  Check,
  X,
  Send,
  Download,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Timesheets() {
  const { user, canManage, isAdmin } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(getWeekStart());
  const [expandedId, setExpandedId] = useState(null);
  const [managerStatusFilter, setManagerStatusFilter] = useState('submitted');

  // Approval dialog
  const [approvalDialog, setApprovalDialog] = useState({ open: false, timesheet: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [approving, setApproving] = useState(false);

  const fetchTimesheets = useCallback(async () => {
    try {
      const params =
        canManage && managerStatusFilter !== 'all'
          ? { status: managerStatusFilter }
          : {};

      const response = await timesheetsAPI.getAll(params);

      const declarations = (response.data || []).map((ts) => ({
        ...ts,
        total_service_hours:
          ts.total_service_hours ??
          ts.total_hours ??
          0,
        total_travel_hours:
          ts.total_travel_hours ??
          0,
        entries: (ts.entries || []).map((entry) => ({
          ...entry,
          service_hours:
            entry.service_hours ??
            entry.worked_hours ??
            0,
          travel_hours:
            entry.travel_hours ??
            0,
          travel_km:
            entry.travel_km ??
            0,
        })),
      }));

      setTimesheets(declarations);
    } catch (err) {
      console.error('Failed to fetch timesheets:', err);
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [canManage, managerStatusFilter]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);


  const handleSubmitTimesheet = async (timesheetId) => {
    try {
      await timesheetsAPI.submit(timesheetId);
      toast.success('Timesheet submitted for approval');
      fetchTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit timesheet');
    }
  };

  const handleApproval = async (approved, timesheetId = null) => {
    if (!approved && !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    const declarationId =
      timesheetId ||
      approvalDialog.timesheet?.id;

    if (!declarationId) {
      toast.error('Timesheet declaration could not be identified');
      return;
    }

    setApproving(true);
    try {
      await timesheetsAPI.approve(
        declarationId,
        approved,
        approved ? null : rejectionReason
      );
      toast.success(`Timesheet ${approved ? 'approved' : 'rejected'}`);
      setApprovalDialog({ open: false, timesheet: null });
      setRejectionReason('');
      fetchTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process approval');
    } finally {
      setApproving(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await exportAPI.timesheetsCSV(selectedWeek);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheets_${selectedWeek}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="timesheets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-heading text-2xl font-bold text-foreground">Timesheets</h1>
        <div className="flex flex-wrap gap-2">
          {!canManage && (
            <div
              className="flex items-center rounded-md border px-3 py-2 text-sm text-muted-foreground"
              data-testid="live-timesheet-status"
            >
              <Clock className="w-4 h-4 mr-2 text-secondary" />
              Updated automatically when you clock off
            </div>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {canManage && (
        <div
          className="flex flex-wrap gap-2"
          data-testid="timesheet-status-filters"
        >
          {[
            { value: 'submitted', label: 'Submitted' },
            { value: 'approved', label: 'Approved' },
            { value: 'exported_to_xero', label: 'Exported' },
            { value: 'all', label: 'All' },
          ].map((filter) => (
            <Button
              key={filter.value}
              type="button"
              size="sm"
              variant={
                managerStatusFilter === filter.value
                  ? 'default'
                  : 'outline'
              }
              onClick={() => {
                setManagerStatusFilter(filter.value);
                setExpandedId(null);
                setLoading(true);
              }}
              data-testid={`timesheet-filter-${filter.value}`}
            >
              {filter.label}
            </Button>
          ))}
        </div>
      )}

      {/* Timesheets List */}
      {timesheets.length > 0 ? (
        <div className="space-y-4">
          {timesheets.map(ts => (
            <Card key={ts.id} data-testid={`timesheet-card-${ts.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-secondary" />
                      Week of {formatDate(ts.week_start)}
                    </CardTitle>
                    {canManage && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Staff: {ts.staff_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusClass(ts.status)}>{capitalize(ts.status)}</Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleExpand(ts.id)}
                      data-testid={`expand-timesheet-${ts.id}`}
                    >
                      {expandedId === ts.id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary stats */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-muted rounded-lg p-3 text-center">
                    <Clock className="w-5 h-5 mx-auto text-secondary mb-1" />
                    <p className="text-lg font-bold">{formatHours(ts.total_service_hours)}</p>
                    <p className="text-xs text-muted-foreground">Service</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3 text-center">
                    <Car className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                    <p className="text-lg font-bold">{formatHours(ts.total_travel_hours)}</p>
                    <p className="text-xs text-muted-foreground">Travel</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3 text-center">
                    <FileText className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                    <p className="text-lg font-bold">{formatDistance(ts.total_km)}</p>
                    <p className="text-xs text-muted-foreground">Distance</p>
                  </div>
                </div>

                {/* Expanded entries */}
                {expandedId === ts.id && ts.entries?.length > 0 && (
                  <div className="overflow-x-auto -mx-4 px-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Travel</TableHead>
                          <TableHead>KM</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ts.entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{formatDate(entry.visit_date)}</TableCell>
                            <TableCell>{entry.client_name}</TableCell>
                            <TableCell>{formatHours(entry.service_hours)}</TableCell>
                            <TableCell>{formatHours(entry.travel_hours)}</TableCell>
                            <TableCell>{entry.travel_km?.toFixed(1)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Rejection reason */}
                {ts.status === 'rejected' && ts.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
                    <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                    <p className="text-sm text-red-700">{ts.rejection_reason}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                  {!canManage && ts.status === 'draft' && (
                    <Button
                      onClick={() => handleSubmitTimesheet(ts.id)}
                      className="bg-secondary hover:bg-secondary/90"
                      data-testid={`submit-timesheet-${ts.id}`}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </Button>
                  )}
                  {!canManage && ts.status === 'rejected' && (
                    <Button
                      onClick={() => handleSubmitTimesheet(ts.id)}
                      className="bg-secondary hover:bg-secondary/90"
                      data-testid={`resubmit-timesheet-${ts.id}`}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Resubmit
                    </Button>
                  )}
                  {canManage && ts.status === 'submitted' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setApprovalDialog({ open: true, timesheet: ts });
                          setRejectionReason('');
                        }}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`reject-timesheet-${ts.id}`}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                      <Button
                        onClick={() => handleApproval(true, ts.id)}
                        disabled={approving}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`approve-timesheet-${ts.id}`}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="empty-state py-12">
            <Clock className="empty-state-icon" />
            <p className="empty-state-title">
              {canManage
                ? `No ${
                    managerStatusFilter === 'all'
                      ? ''
                      : managerStatusFilter.replaceAll('_', ' ')
                  } timesheets`
                : 'No Timesheets'}
            </p>
            <p className="empty-state-description">
              {canManage 
                ? 'No timesheets pending approval' 
                : 'Completed visits will appear here automatically after you clock off'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Rejection Dialog */}
      <Dialog open={approvalDialog.open} onOpenChange={(open) => setApprovalDialog({ open, timesheet: approvalDialog.timesheet })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Timesheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Reason for Rejection *</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this timesheet is being rejected..."
                rows={4}
                data-testid="rejection-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setApprovalDialog({ open: false, timesheet: null })}>
                Cancel
              </Button>
              <Button
                onClick={() => handleApproval(false)}
                disabled={approving}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-reject-btn"
              >
                {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Reject Timesheet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
